/**
 * The reference's own derived values, ported.
 *
 * Every string the screens show that is not read straight off a record is computed here, by the
 * same rule the prototype uses. The rules are ported rather than reinvented and the source line
 * is named in each block, because the acceptance threshold is a pixel diff: a status label, a
 * month name or a thousands separator that differs by one character is a failed screen.
 *
 * `PROTOTYPE_TODAY` is the reason the DEMO is reproducible at all. Every "is this expired",
 * "how many this month" and "expiring soon" answer in the reference is relative to a FIXED date
 * baked into the seed (`m01-utils.js`). Using the real clock there would make the dashboard's own
 * numbers drift away from the reference's the day after they were captured.
 *
 * The product's answers are relative to the day it is actually being used on. Which of the two a
 * screen gets is a mounting decision, not a constant every screen imports — see the view clock
 * below.
 */
import type {
  BatchSummary,
  DemoBatch,
  DemoCredential,
  DemoDatabase,
  DemoPermissions,
  DemoRelease,
  DemoTemplate,
  DemoValidityPolicy,
} from '../data/types';

/** `m01-utils.js` — the prototype's fixed date. The demo pins the view clock to it. */
export const PROTOTYPE_TODAY = '2026-09-02';
export const PROTOTYPE_VERSION = 'v0.7';

export const TR_MONTHS = [
  'Ocak',
  'Şubat',
  'Mart',
  'Nisan',
  'Mayıs',
  'Haziran',
  'Temmuz',
  'Ağustos',
  'Eylül',
  'Ekim',
  'Kasım',
  'Aralık',
] as const;

export const EN_MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

const pad2 = (n: number): string => String(n).padStart(2, '0');

// ------------------------------------------------------------------------------- the view clock

/**
 * Two surfaces read these screens and they do not share a today.
 *
 * The PRODUCT has to answer "is this expired", "how many this month" and "expiring soon" about the
 * day it is actually being used on: a certificate that lapsed last week cannot read «Aktif»
 * because a constant in the source says otherwise. The DEMO has to answer them about a FIXED day,
 * because the acceptance threshold is a pixel diff against the frozen reference and the
 * reference's own seed is relative to `PROTOTYPE_TODAY` — a real clock would walk the dashboard's
 * numbers away from it overnight.
 *
 * So the date is a value the mounting surface chooses. The demo provider pins it — `data/provider.tsx`,
 * the one module in the screen layer that loads the seed and the one the production graph cannot
 * reach (`scripts/check-demo-isolation.mjs`) — and the product never calls `pinViewDate` at all,
 * which `scripts/check-invariants.mjs` enforces. Unpinned means the real clock.
 *
 * Read per call, never cached at module load: a console left open across midnight must not keep
 * offering yesterday's date to a new form, and a panel refreshed after midnight must not keep
 * counting yesterday's month.
 */
let pinnedDate: string | null = null;

/** Pin the view clock to one calendar date. The demo's seed provider is the only caller. */
export function pinViewDate(iso: string): void {
  pinnedDate = iso;
}

/** Whether a fixed date is in force — the one thing a screen may phrase differently because of it. */
export const viewDateIsPinned = (): boolean => pinnedDate !== null;

/** Calendar date (UTC) of an instant — the backend's `dateOnlyUTC`, `domain/dates.ts`. */
function dateOnlyUTC(d: Date): string {
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
}

/**
 * Today, decided the way the backend decides it.
 *
 * `domain/credential/state.ts` asks `expiresAt < today` where today is `dateOnlyUTC(clock.now())`,
 * and `domain/dates.ts` states the rule that comparison rests on: DateOnly values carry no
 * timezone and are compared lexicographically as `YYYY-MM-DD`. Reading the browser's LOCAL date
 * instead would put this surface a day ahead of the server for three hours every night in Türkiye
 * — the same class of disagreement this clock exists to remove. So: the UTC calendar date, and the
 * same strict `<` at the call site. The last day of validity is inclusive on both sides.
 */
export function todayIso(): string {
  return pinnedDate ?? dateOnlyUTC(new Date());
}

/**
 * The instant a record written on this surface carries, to the second.
 *
 * `prototypeNowUTC` in the reference (`m01-utils.js:105`) takes the date from the fixed day and
 * only the time of day from the real clock, so a record touched during the demo sorts alongside
 * the seed instead of a year after it. That is still exactly what the demo gets. In the product
 * both halves come from one real instant — a single `Date` read, so the date and the time can
 * never straddle midnight between them.
 */
export function nowUtcSeconds(): string {
  const d = new Date();
  const date = pinnedDate ?? dateOnlyUTC(d);
  return `${date}T${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}:${pad2(d.getUTCSeconds())}Z`;
}

/** Turkish case: `toUpperCase()` maps i→I, which is wrong here. `m01-utils.js`. */
export const trUpper = (s: string): string =>
  String(s ?? '')
    .replace(/i/g, 'İ')
    .replace(/ı/g, 'I')
    .toUpperCase();

export const trLower = (s: string): string =>
  String(s ?? '')
    .replace(/İ/g, 'i')
    .replace(/I/g, 'ı')
    .toLowerCase();

/** Noon, so a timezone shift cannot move a date-only value onto the previous day. */
export function parseISO(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12);
}

export type DateMode = 'long' | 'short' | 'month' | 'monthName' | 'year';

/**
 * A value read back to the calendar day it names, or null when it names none.
 *
 * WHY A READ-BACK EXISTS
 * ----------------------
 * A date field's value reaches the drawing as TEXT. The field catalogue's own sample used to be the
 * words «2 Eylül 2026» rather than a date, every template built from it carries that, and the editor's
 * test-data box holds whatever a person typed there. None of that can be redrawn in another language
 * unless it can first be read as a day — which is why the English tab used to draw the Turkish words.
 *
 * So a canonical `YYYY-MM-DD` passes through, and the long form in either of the product's two
 * languages is read back to the day it names. **Nothing else is.** A value that is not one of those
 * shapes is not a date, and the caller draws it exactly as it stands: free text a person typed keeps
 * its own words, and today's date is never put in the place of something unreadable.
 *
 * The month tables below are `TR_MONTHS`/`EN_MONTHS`, so the words this reads are the same words
 * `fmtDate` writes. The server's own copy of this rule is
 * `backend/src/domain/credential/date-display.ts`, and the two run
 * `tests/fixtures/credential-date-display-vectors.json` together.
 */
export function readDateOnly(value: unknown): string | null {
  const d = readCalendarParts(value);
  return d === null ? null : `${d.year}-${pad2(d.month)}-${pad2(d.day)}`;
}

/** Exactly a canonical date-only value, with nothing before or after it. */
const DATE_ONLY_RX = /^(\d{4})-(\d{2})-(\d{2})$/;
/**
 * A COMPLETE timestamp, by the product's own datetime rule — RFC3339 with a mandatory zone
 * designator (`backend/src/domain/dates.ts`, `parseDateTimeStrict`). Kept the same shape on purpose:
 * one product, one idea of what a timestamp is.
 *
 * The zone offset is CAPTURED rather than skipped, because its own hours and minutes have to be
 * real: matching the shape `[+-]\d{2}:\d{2}` accepted `+99:99`, `+24:00`, `+03:60` and `-00:60`,
 * none of which the server accepts. `Z` has no numbers to check.
 */
const TIMESTAMP_RX =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,9})?)?(?:Z|([+-])(\d{2}):(\d{2}))$/;

/**
 * THE YEARS A TIMESTAMP MAY NAME — the server's own bounds, for the timestamp form only.
 *
 * `parseDateTimeStrict` hands its date part to `fromParts`, which refuses a year outside
 * `MIN_YEAR`…`MAX_YEAR` (`backend/src/domain/dates.ts`). The browser has no such helper, so it
 * carries the same two numbers, and `tests/fixtures/credential-date-display-vectors.json` holds them
 * once: both packages assert their own constants against that file, so neither can drift alone.
 *
 * A bare `YYYY-MM-DD` and the two long forms have NO year bound on either side — the server's
 * date-only branch and `realDate` check the calendar, not the century — and nothing here adds one.
 */
const TIMESTAMP_MIN_YEAR = 1900;
const TIMESTAMP_MAX_YEAR = 2200;

interface CalendarParts {
  year: number;
  month: number;
  day: number;
}

/**
 * The calendar day a value names, or null when it names none.
 *
 * THE WHOLE STRING HAS TO BE ONE DATE.
 *
 * The ISO branch used to match the LEADING `YYYY-MM-DD` and ignore whatever came after it, so
 * «2026-09-02XYZ» and «2026-09-02 / 2026-09-03» — the second of which names two days — were drawn as
 * 2 September 2026, and «2026-09-02T99:99:99Z», whose hour does not exist, was drawn as a date too.
 * Dropping the rest of a string and presenting what is left as the answer is this product inventing
 * one. A canonical date passes, a complete timestamp passes and is read as the day it WRITES, the two
 * long forms are read back, and nothing else is a date.
 *
 * Issuance, import and correction are untouched: a claim still goes through the server's own
 * `validateFieldValue`, which normalizes a date field to its canonical `YYYY-MM-DD` before anything is
 * stored. What reaches this reader unvalidated is what a person typed into the editor's test-data box,
 * a field catalogue's sample, or a template's own default.
 */
function readCalendarParts(value: unknown): CalendarParts | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (text === '') return null;
  const iso = DATE_ONLY_RX.exec(text) ?? readTimestamp(text);
  if (iso) return realDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));
  /* «2 Eylül 2026» / «2 September 2026» — day, month name, year, and nothing else around them. */
  const spelled = /^(\d{1,2})\s+(\S+)\s+(\d{4})$/.exec(text);
  if (!spelled) return null;
  /* The month tables are `readonly` tuples of literals, so they are searched as plain strings. */
  const name = spelled[2] as string;
  const names: readonly string[] = [...TR_MONTHS, ...EN_MONTHS];
  const at = names.indexOf(name);
  if (at === -1) return null;
  return realDate(Number(spelled[3]), (at % 12) + 1, Number(spelled[1]));
}

/**
 * The date part of a complete timestamp the server would also accept, or null.
 *
 * Shape, then numbers: the clock has to read as a real time of day, the zone offset has to be a real
 * offset, and the year has to be one the server's own `fromParts` allows. Any of those failing means
 * the value is not a timestamp — it is drawn exactly as it stands, not narrowed to its first ten
 * characters, because «2026-09-02T21:30:00+24:00» names no moment and answering it with
 * «2 September 2026» would be this product inventing one.
 *
 * No `Date` here either: a bad offset is caught by reading its digits. Handing the string to
 * `Date.parse` would work for the server (it does exactly that) but would put an instant back into
 * the path this file deliberately keeps free of one.
 */
function readTimestamp(text: string): RegExpExecArray | null {
  const m = TIMESTAMP_RX.exec(text);
  if (!m) return null;
  if (Number(m[4]) > 23 || Number(m[5]) > 59 || Number(m[6] ?? '0') > 59) return null;
  /* `m[7]` is the offset's sign; it is present only when the zone was not `Z`. */
  if (m[7] !== undefined && (Number(m[8]) > 23 || Number(m[9]) > 59)) return null;
  const year = Number(m[1]);
  if (year < TIMESTAMP_MIN_YEAR || year > TIMESTAMP_MAX_YEAR) return null;
  return m;
}

/** The timestamp bounds this module enforces, for the test that ties them to the shared contract. */
export const TIMESTAMP_CONTRACT = Object.freeze({
  yearRange: Object.freeze({ min: TIMESTAMP_MIN_YEAR, max: TIMESTAMP_MAX_YEAR }),
  offsetRange: Object.freeze({ maxHour: 23, maxMinute: 59 }),
});

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/** The components of a day the calendar really has, or null. Nothing is clamped or rolled. */
function realDate(year: number, month: number, day: number): CalendarParts | null {
  if (month < 1 || month > 12) return null;
  const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  const max = month === 2 && leap ? 29 : (DAYS_IN_MONTH[month - 1] ?? 0);
  if (day < 1 || day > max) return null;
  return { year, month, day };
}

/**
 * A date-typed field's value, in the language it is being DRAWN in.
 *
 * The value itself is not changed anywhere — this is the text, and only the text. A value that is not
 * a date comes back untouched, which is what keeps a free-text answer from being rewritten as a day.
 *
 * NO `Date` IN THIS PATH. The text is composed from the validated year, month and day and the
 * product's own month names. `fmtDate` would reach it through `parseISO`, which builds
 * `new Date(y, m - 1, d, 12)` — a LOCAL instant — and noon does not save a day the local calendar
 * does not have: Samoa went from 29 December 2011 straight to 31 December, so in `Pacific/Apia` the
 * runtime answers «31 December» for the 30th. A certificate issued on the 30th would be drawn as the
 * 31st for a reader whose machine is set to that zone. `fmtDate` and `parseISO` are left exactly as
 * they are — every other screen keeps its behaviour — and only this path is built from components.
 */
export const localizedDateText = (value: unknown, lang: 'tr' | 'en'): string => {
  const d = readCalendarParts(value);
  if (d === null) return typeof value === 'string' ? value : '';
  const month = (lang === 'en' ? EN_MONTHS : TR_MONTHS)[d.month - 1] ?? '';
  return `${d.day} ${month} ${d.year}`;
};

/**
 * THE FIELDS WHOSE VALUE IS DRAWN AS A LOCALIZED DATE — the server's set, not a wider one.
 *
 * The server formats exactly these two auto keys (`autoFieldValue` in
 * `backend/src/domain/issuance/plan.ts`) and hands every other field its claim verbatim. The preview
 * used to format EVERY field whose `dataType` was `date`, so a template with its own `course_date`
 * showed «2 September 2026» on the English tab while the document it sealed said `2026-09-02` — the
 * preview promising something the seal does not keep.
 *
 * Keyed by field key rather than by `dataType`, because the key is what the server switches on. The
 * list is checked against `tests/fixtures/credential-date-display-vectors.json`, which both packages
 * read, so neither side can widen it alone.
 */
export const LOCALIZED_DATE_FIELD_KEYS: readonly string[] = ['issue_date', 'expiry_date'];

export const isLocalizedDateField = (key: string): boolean => LOCALIZED_DATE_FIELD_KEYS.includes(key);

export function fmtDate(
  iso: string | null | undefined,
  lang: 'tr' | 'en' = 'tr',
  mode: DateMode = 'long',
): string {
  const d = parseISO(iso);
  if (!d) return '—';
  const M = lang === 'en' ? EN_MONTHS : TR_MONTHS;
  const month = M[d.getMonth()] ?? '';
  if (mode === 'month') return `${month} ${d.getFullYear()}`;
  if (mode === 'monthName') return month;
  if (mode === 'year') return String(d.getFullYear());
  if (mode === 'short') return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${d.getFullYear()}`;
  return `${d.getDate()} ${month} ${d.getFullYear()}`;
}

export function fmtDateTime(iso: string | null | undefined, lang: 'tr' | 'en' = 'tr'): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const M = lang === 'en' ? EN_MONTHS : TR_MONTHS;
  return `${d.getDate()} ${(M[d.getMonth()] ?? '').slice(0, 3)} ${d.getFullYear()} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export const fmtNum = (n: number): string => Number(n).toLocaleString('tr-TR');

/** `m01d-dates.js` clamps to the end of the month; the demo only needs the clamped result. */
export function addMonths(iso: string, n: number): string | null {
  const d = parseISO(iso);
  if (!d) return null;
  const year = d.getFullYear();
  const month = d.getMonth() + n;
  const target = new Date(year, month, 1, 12);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0, 12).getDate();
  const day = Math.min(d.getDate(), lastDay);
  return `${target.getFullYear()}-${pad2(target.getMonth() + 1)}-${pad2(day)}`;
}

// ---------------------------------------------------------------- state labels (`m03-state.js`)

export type ViewState =
  | 'draft'
  | 'pending_acceptance'
  | 'pending_admin_publish'
  | 'active'
  | 'expired'
  | 'revoked'
  | 'superseded';

export const STATUS_TR: Record<ViewState, string> = {
  draft: 'Taslak',
  pending_acceptance: 'Onay bekliyor',
  pending_admin_publish: 'Yayın onayı bekliyor',
  active: 'Aktif',
  expired: 'Süresi dolmuş',
  revoked: 'İptal edilmiş',
  superseded: 'Yerine yenisi düzenlendi',
};

export const STATUS_PILL: Record<ViewState, string> = {
  draft: 'pill-neutral',
  pending_acceptance: 'pill-info',
  pending_admin_publish: 'pill-info',
  active: 'pill-active',
  expired: 'pill-expired',
  revoked: 'pill-revoked',
  superseded: 'pill-revoked',
};

export const STATUS_ICON: Record<ViewState, string> = {
  draft: '·',
  pending_acceptance: '◔',
  pending_admin_publish: '◔',
  active: '✓',
  expired: '○',
  revoked: '✕',
  superseded: '↷',
};

export const DELIVERY_TR: Record<string, [string, string]> = {
  queued: ['Kuyrukta', 'pill-info'],
  sent: ['Gönderildi', 'pill-neutral'],
  delivered: ['Teslim edildi', 'pill-active'],
  bounced: ['Geri döndü', 'pill-danger'],
  failed: ['Başarısız', 'pill-danger'],
  disabled: ['Gönderilmedi', 'pill-neutral'],
};

/**
 * The «E-posta» pill, including the two values the reference's own table has no row for.
 *
 * The reference's fallback for an unrecognised VALUE is kept: `created` and `superseded` are stages of
 * a send that is under way, and «Kuyrukta» is the word the reference puts on those. What is not kept is
 * reading «this record has no e-mail» as «queued». `null` is what the server sends for a record with no
 * send at all — no claim attempt and no notification — and the register's own filter spells that same
 * thing `none`. Both are the reference's «Gönderilmedi»: saying «Kuyrukta» about an e-mail nothing will
 * ever send is the same class of statement as a total nobody counted.
 *
 * The VALUES are the server's, and the server maps a notification's `email_delivery_status` into this
 * same vocabulary before sending it (`latestDeliverySql`), so this table stays the one that was here:
 * it is not a second mapping, and a bounce never arrives spelled `hard_bounced`.
 *
 * `pending` is not here on purpose: it is the panel link's spelling of `queued`, and the reference's
 * fallback already reads it as «Kuyrukta», which is what it means.
 */
export function deliveryLabel(d: string | null): [string, string] {
  const key = d === null || d === 'none' ? 'disabled' : d;
  return DELIVERY_TR[key] ?? DELIVERY_TR.queued ?? ['Kuyrukta', 'pill-info'];
}

export const EVENT_TR: Record<string, string> = {
  created: 'Kayıt oluşturuldu',
  rendered: 'Görsel render edildi',
  email_queued: 'E-posta kuyruğa alındı (simülasyon)',
  email_sent: 'E-posta gönderildi (simülasyon)',
  delivered: 'E-posta teslim edildi (simülasyon)',
  bounced: 'E-posta geri döndü (simülasyon)',
  claim_opened: 'Alıcı onay sayfasını açtı',
  accepted: 'Alıcı onayladı',
  published: 'Public sayfa yayınlandı',
  admin_published: 'Yönetici yayınladı',
  corrected: 'Kayıt düzeltildi',
  revised: 'Kayıt revize edildi',
  resent: 'E-posta yeniden gönderildi (simülasyon)',
  revoked: 'Credential iptal edildi',
  reissued: 'Yerine yeni kayıt düzenlendi',
  superseded_by: 'Yerine yeni kayıt geçti',
  downloaded: 'Görsel indirildi',
  linkedin_helper_opened: 'LinkedIn profil yardımcısı açıldı',
  linkedin_share_opened: 'LinkedIn paylaşım akışı açıldı',
  error_reported: 'Alıcı hata bildirdi',
  contact_email_updated: 'İletişim e-postası güncellendi',
};

export const TEMPLATE_STATUS_TR: Record<string, string> = {
  draft: 'Taslak',
  published: 'Yayında',
  archived: 'Arşiv',
};

/**
 * The derived view state — `credentialViewState` in `m03-state.js`.
 *
 * Two rules that the stored `state` column does not carry on its own: a revoked record that has a
 * successor reads as `superseded`, and an active record whose validity has lapsed reads as
 * `expired`. Both are computed, never stored.
 *
 * A third case outranks both, and it is the reason the public page can be trusted: when the
 * projection a record came from already carried the SERVER's own verdict, that verdict is the
 * answer and nothing here recomputes it. `/api/v1/public/credentials/:code` decides `active`,
 * `expired`, `revoked` or `superseded` against the issuer's clock, and a browser is in no position
 * to overrule it — a visitor whose device is set to last month would otherwise read «Aktif» on a
 * page the issuer's own server calls expired, and one set to next year the reverse. Internal
 * screens carry no verdict: the internal projections serialise the STORED state (`api/types.ts`),
 * so for those the two rules below still do the deriving — now against the real date.
 */
export function credentialViewState(c: DemoCredential): ViewState {
  if (c.serverViewState) return c.serverViewState;
  if (c.state === 'revoked') return c.supersededByCode ? 'superseded' : 'revoked';
  if (c.state === 'active' && c.expiresAt && c.expiresAt < todayIso()) return 'expired';
  return c.state as ViewState;
}

export const displayName = (c: DemoCredential): string =>
  String((c.subjectSnapshot as { canonicalDisplayName?: string }).canonicalDisplayName ?? '');

export const publicUrl = (c: DemoCredential): string => c.canonicalPublicUrl;
export const imageUrl = (c: DemoCredential): string => `${c.canonicalPublicUrl}/image.png`;
export const printUrl = (c: DemoCredential): string => `${c.canonicalPublicUrl}/print`;
export const ogUrl = (c: DemoCredential): string => `${c.canonicalPublicUrl}/og.png`;

interface LocalizedMetadata {
  title?: string;
  description?: string;
  criteria?: string;
  issuerStatement?: string;
}

/**
 * The release's metadata IN THE LANGUAGE ASKED FOR, and in no other.
 *
 * The `?? meta.tr` that used to stand here was a silent fallback: a release with no English name
 * answered every English question with its Turkish one, so the English tab showed a Turkish title
 * and called it English. An absent language now reads as absent, and the surfaces say so.
 */
function localized(c: DemoCredential, lang: 'tr' | 'en'): LocalizedMetadata {
  const meta = c.release.localizedMetadata ?? {};
  return meta[lang === 'en' ? 'en' : 'tr'] ?? {};
}

export const credTitle = (c: DemoCredential, lang: 'tr' | 'en' = 'tr'): string =>
  localized(c, lang).title ?? '';
export const credDescription = (c: DemoCredential, lang: 'tr' | 'en' = 'tr'): string =>
  localized(c, lang).description ?? '';
export const credCriteria = (c: DemoCredential, lang: 'tr' | 'en' = 'tr'): string =>
  localized(c, lang).criteria ?? '';
export const credIssuerStatement = (c: DemoCredential, lang: 'tr' | 'en' = 'tr'): string =>
  localized(c, lang).issuerStatement ?? '';

export const credType = (c: DemoCredential): string => c.release.credentialType;

export const credPermissions = (c: DemoCredential): DemoPermissions => c.release.permissions;

export const currentRelease = (t: DemoTemplate | undefined): DemoRelease | null => {
  if (!t?.currentReleaseId) return null;
  return t.releases.find((r) => r.id === t.currentReleaseId) ?? null;
};

// ---------------------------------------------------------------- aggregates (`m03-state.js`)

export type { BatchSummary } from '../data/types';

/**
 * The batch's own numbers.
 *
 * Counted from the batch's rows and the credentials they produced — which is right when the whole
 * world is in the tab, and impossible when it is a page of it. A batch that arrived from a LIST
 * carries the counts the server took instead (`summary`), and they are returned untouched: the
 * history table's «Satır · Oluşturulan · Hatalı · Onay bekleyen» are then counts over the batch,
 * not over the part of it that happened to be downloaded. The batch that is actually OPEN is read
 * with its rows, and counts them here as before.
 */
/**
 * What a batch row's stored status MEANS, in one place, for two dialects of the same fact.
 *
 * The reference fixture writes `invalid` for a row that was rejected. The backend's
 * `issuance_row_status` has no such value: a rejected row is `needs_resolution` (it can be corrected
 * and re-run) or `failed` (the issuance itself failed). The screen recognised only the fixture's
 * word, so against the real backend a rejected row was counted as nothing, drawn with no pill and
 * left out of «hatalı satırları indir» — the one row on the screen that most needed saying.
 *
 * `issued`, `planned` and `pending` are not folded together: a row that has not run yet is not a row
 * that produced nothing.
 */
export type BatchRowOutcome = 'invalid' | 'duplicate' | 'skipped' | 'done';

export function batchRowOutcome(status: string | undefined): BatchRowOutcome {
  if (status === 'invalid' || status === 'needs_resolution' || status === 'failed') return 'invalid';
  if (status === 'skipped_duplicate') return 'duplicate';
  if (status === 'skipped') return 'skipped';
  return 'done';
}

export function batchSummary(b: DemoBatch, db: DemoDatabase): BatchSummary {
  if (b.summary) return b.summary;
  const s: BatchSummary = {
    /* The batch's rows, not the page of them that is on screen, when the read says how many there are. */
    total: typeof b.rowsTotal === 'number' ? b.rowsTotal : b.rows.length,
    created: 0,
    invalid: 0,
    duplicates: 0,
    skipped: 0,
    pending: 0,
    pendingAdmin: 0,
    active: 0,
    expired: 0,
    revoked: 0,
    superseded: 0,
    delivered: 0,
    bounced: 0,
    queued: 0,
    sent: 0,
    emailDisabled: 0,
  };
  for (const row of b.rows) {
    const r = row as { status?: string; credentialId?: string | null };
    const outcome = batchRowOutcome(r.status);
    if (outcome === 'invalid') s.invalid++;
    else if (outcome === 'duplicate') s.duplicates++;
    else if (outcome === 'skipped') s.skipped++;
    const c = r.credentialId ? db.credentials.find((x) => x.id === r.credentialId) : null;
    if (!c) continue;
    s.created++;
    const st = credentialViewState(c);
    if (st === 'pending_acceptance') s.pending++;
    else if (st === 'pending_admin_publish') s.pendingAdmin++;
    else if (st === 'active') s.active++;
    else if (st === 'expired') s.expired++;
    else if (st === 'revoked') s.revoked++;
    else if (st === 'superseded') s.superseded++;
    if (c.deliveryStatus === 'delivered') s.delivered++;
    else if (c.deliveryStatus === 'bounced') s.bounced++;
    else if (c.deliveryStatus === 'sent') s.sent++;
    else if (c.deliveryStatus === 'disabled') s.emailDisabled++;
    else s.queued++;
  }
  return s;
}

export function batchCredentials(b: DemoBatch, db: DemoDatabase): DemoCredential[] {
  const out: DemoCredential[] = [];
  for (const row of b.rows) {
    const id = (row as { credentialId?: string | null }).credentialId;
    if (!id) continue;
    const c = db.credentials.find((x) => x.id === id);
    if (c) out.push(c);
  }
  return out;
}

export interface DashboardStats {
  st: Record<ViewState, number>;
  total: number;
  thisMonth: number;
  bounced: number;
  queued: number;
  templates: number;
  published: number;
}

export function publishedTemplates(db: DemoDatabase): DemoTemplate[] {
  return db.templates.filter((t) => t.status === 'published' && t.currentReleaseId);
}

export function dashboardStats(db: DemoDatabase): DashboardStats {
  const st: Record<ViewState, number> = {
    draft: 0,
    pending_acceptance: 0,
    pending_admin_publish: 0,
    active: 0,
    expired: 0,
    revoked: 0,
    superseded: 0,
  };
  for (const c of db.credentials) {
    const s = credentialViewState(c);
    st[s] = (st[s] ?? 0) + 1;
  }
  const ym = todayIso().slice(0, 7);
  return {
    st,
    total: db.credentials.length,
    thisMonth: db.credentials.filter((c) => c.issueDate.startsWith(ym)).length,
    bounced: db.credentials.filter((c) => c.deliveryStatus === 'bounced').length,
    queued: db.credentials.filter((c) => c.deliveryStatus === 'queued').length,
    templates: db.templates.length,
    published: publishedTemplates(db).length,
  };
}

// ------------------------------------------- field, policy and type vocabularies (`m01c`, `m02`)

/**
 * `VISIBILITY_TR` — `m01c-validate.js:9`, verbatim.
 *
 * Four values, not two booleans: whether a value reaches the public detail list and whether it may
 * be bound to a render layer are separate permissions, and a private field can be neither.
 */
export const VISIBILITY_TR: Record<string, string> = {
  private: 'Gizli (yalnız yönetim)',
  public_metadata: 'Public detay listesi',
  public_render: 'Yalnız görsel',
  public_metadata_and_render: 'Detay listesi + görsel',
};

/** `FIELD_DATA_TYPES` — `m01c-validate.js:17`. `renderable` decides what a layer may bind. */
export const FIELD_DATA_TYPES: { key: string; tr: string; renderable: boolean }[] = [
  { key: 'string', tr: 'Kısa metin', renderable: true },
  { key: 'text', tr: 'Uzun metin', renderable: true },
  { key: 'email', tr: 'E-posta', renderable: false },
  { key: 'url', tr: 'URL', renderable: true },
  { key: 'number', tr: 'Sayı', renderable: true },
  { key: 'integer', tr: 'Tam sayı', renderable: true },
  { key: 'date', tr: 'Tarih', renderable: true },
  { key: 'datetime', tr: 'Tarih ve saat', renderable: true },
  { key: 'boolean', tr: 'Evet / Hayır', renderable: true },
  { key: 'enum', tr: 'Seçenek listesi', renderable: true },
  { key: 'credential_id', tr: 'Credential ID', renderable: true },
  { key: 'credential_url', tr: 'Credential URL', renderable: true },
  { key: 'qr', tr: 'QR kod', renderable: true },
];

export const dataTypeRec = (k: string | undefined): { key: string; tr: string; renderable: boolean } =>
  FIELD_DATA_TYPES.find((x) => x.key === k) ??
  (FIELD_DATA_TYPES[0] as { key: string; tr: string; renderable: boolean });

/** `PUBLICATION_POLICIES` — `m02-mockdb.js:31`, with the reference's own help text. */
export const PUBLICATION_POLICIES: { key: string; tr: string; help: string }[] = [
  {
    key: 'require_recipient_acceptance',
    tr: 'Alıcı onayı gerekir',
    help: 'Kayıt pending_acceptance durumunda açılır; alıcı geçerli claim bağlantısıyla onaylayınca yayınlanır.',
  },
  {
    key: 'issue_immediately',
    tr: 'Üretilir üretilmez yayınlanır',
    help: 'Kayıt issuance işleminin sonunda doğrudan active/public olur.',
  },
  {
    key: 'admin_publish',
    tr: 'Yöneticinin yayınlaması gerekir',
    help: 'Kayıt pending_admin_publish durumunda bekler; yönetici komutuyla yayınlanır.',
  },
];

export const pubPolicyRec = (k: string | undefined): { key: string; tr: string; help: string } =>
  PUBLICATION_POLICIES.find((x) => x.key === k) ??
  (PUBLICATION_POLICIES[0] as { key: string; tr: string; help: string });

/** `DUPLICATE_ACTIONS` — `m02-mockdb.js:37`. */
export const DUPLICATE_ACTIONS: { key: string; tr: string }[] = [
  { key: 'skip', tr: 'Mükerreri atla' },
  { key: 'allow', tr: 'Yine de üret' },
  { key: 'review', tr: 'Tek tek incele' },
  { key: 'reject', tr: 'Satırı hata say' },
];

export const SUBJECT_TYPE_TR: Record<string, string> = {
  person: 'Kişi',
  organization: 'Şirket / kurum',
};

/** `fieldLabel` — `m02-mockdb.js:102`. Falls back to the key, never to an empty label. */
export const fieldLabel = (
  f: { key: string; labels?: Record<string, string> | null },
  lang: 'tr' | 'en' = 'tr',
): string => (f.labels ? (lang === 'en' ? f.labels.en : f.labels.tr) : null) || f.key;

/**
 * `editableFields` — `m08b-admin2.js:41`.
 *
 * An `auto` field is filled by the system (the public code, the QR, the issue date), so issuance
 * does not ask for it. `certification_period` is the one exception upstream makes.
 */
export const editableFields = <T extends { key: string; auto?: boolean }>(fields: T[]): T[] =>
  fields.filter((f) => !f.auto || f.key === 'certification_period');

/**
 * `nextExpiry` — `m03-state.js:223`.
 *
 * `months` clamps to the end of a shorter month (31 Ocak + 1 ay → 28/29 Şubat), `fixed` takes the
 * release's own date, and `none` means the record never lapses.
 */
export function nextExpiry(
  validityPolicy: DemoValidityPolicy | null | undefined,
  issueDate: string,
): string | null {
  const v = validityPolicy ?? { mode: 'none' };
  if (v.mode === 'months' && v.months) return addMonths(issueDate, v.months);
  if (v.mode === 'fixed' && v.fixedUntil) return v.fixedUntil;
  return null;
}

/** `stateForPolicy` — `m03-state.js:228`. Which state a record opens in. */
export const stateForPolicy = (policy: string | undefined): ViewState =>
  policy === 'issue_immediately'
    ? 'active'
    : policy === 'admin_publish'
      ? 'pending_admin_publish'
      : 'pending_acceptance';
